import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Login.css";
import RegisterPopup from "./RegisterPopup";
import EmailVerificationPopup from "./EmailVerificationPopup";
import ForgotPassword from "./ForgotPasswordPopup";
import ReCAPTCHA from "react-google-recaptcha";

// Google Login
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

import WelcomeVideoPopup from "./WelcomeVideoPopup";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const apiArrayGetir = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.$values)) return value.$values;
  return [];
};

const kullaniciShiftYetkiliMi = (user) => {
  const permissionCodes = apiArrayGetir(user?.permissionCodes).map((x) =>
    String(x).trim().toLowerCase()
  );

  const deniedPermissionCodes = apiArrayGetir(user?.deniedPermissionCodes).map((x) =>
    String(x).trim().toLowerCase()
  );

  const shiftIzniVar = permissionCodes.includes("shift.yonetimi");
  const shiftIzniEngelliMi = deniedPermissionCodes.includes("shift.yonetimi");

  return shiftIzniVar && !shiftIzniEngelliMi;
};

export default function Login({ onClose }) {
  const navigate = useNavigate();

  // 👇 Hoşgeldin videosu state
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  // 🔁 Login sonrası sayfaya geçiş fonksiyonu
  const proceedToApp = () => {
    const userRole = localStorage.getItem("userRole");
    const targetPath = userRole === "personel" ? "/profil" : "/";

    navigate(targetPath);

    setTimeout(() => {
      onClose();
      window.location.reload();
    }, 1000);
  };



  // 📺 Login başarılı olduktan sonra hoşgeldin videosu kontrolü
  const checkWelcomeVideo = async (token) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/GirisVideo/kullanici/hepsi`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const list =
        res.data.$values ||
        res.data.values ||
        res.data.sValues ||
        res.data ||
        [];

      // Sadece hoşgeldin videosunu bul
      const kayit = list.find((item) => String(item.videoId) === "1");

      console.log("👉 GirisVideo kayit:", kayit);

      if (!kayit) {
        // 1️⃣ Hiç kayıt yok → ilk giriş
        await axios.post(
          `${API_BASE_URL}/api/GirisVideo/ekle`,
          {
            videoAdi: "hosgeldin",
            videoId: "1",
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Kayıt açıldı, videoyu göster
        setShowWelcomeVideo(true);
        return;
      }

      // 2️⃣ Kayıt var → izledi & birdahaSorma kontrolü
      const izledi = kayit.izledi === true;              // ❗ DİKKAT: izledi
      const birdahaSorma = kayit.birdahaSorma === true;  // ❗ aynen böyle

      console.log("izledi:", izledi, "birdahaSorma:", birdahaSorma);

      if (izledi && birdahaSorma) {
        // ✅ Video tam izlenmiş ve bir daha sorma denmiş → video açma
        proceedToApp();
      } else {
        // ❗ Yarım kalmış veya birdahaSorma false → tekrar video oynat
        setShowWelcomeVideo(true);
      }
    } catch (err) {
      console.error("GirisVideo kontrol hatası:", err);
      // Hata olursa yine uygulamaya al
      proceedToApp();
    }
  };


  // Login popup açıldığında kayıtlı login bilgisi varsa doldur
  useEffect(() => {
    const savedEmail = localStorage.getItem("loginEmail");
    const savedPassword = localStorage.getItem("loginPassword");
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  // Eski kayıtlı login bilgilerini temizleme (senin kodundan)
  useEffect(() => {
    const savedEmail = localStorage.getItem("loginEmail");
    const savedPassword = localStorage.getItem("loginPassword");
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      localStorage.removeItem("loginEmail");
      localStorage.removeItem("loginPassword");
    }
  }, []);

  // Enter ile login
  useEffect(() => {
    if (showRegisterPopup || showVerificationPopup || showForgotPassword || showWelcomeVideo) {
      return;
    }

    const handleEnter = (e) => {
      if (e.key === "Enter") {
        handleLogin();
      }
    };

    document.addEventListener("keydown", handleEnter);
    return () => document.removeEventListener("keydown", handleEnter);
  }, [
    email,
    password,
    showRegisterPopup,
    showVerificationPopup,
    showForgotPassword,
    showWelcomeVideo,
  ]);

  const handleLogin = async () => {
    setLoading(true);
    setError("");

    if (!email || !password) {
      setError("⚠️ E-posta/telefon ve şifre gerekli!");
      setLoading(false);
      return;
    }

    const isDevelopment =
      window.location.hostname === "localhost" ||
      window.location.hostname.startsWith("192.168.");

    if (!captchaVerified && !isDevelopment) {
      setError("⚠️ Lütfen 'Ben robot değilim' doğrulamasını tamamlayın.");
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            identifier: email,
            password,
            saglayiciId: "0",
          }),
        }
      );

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text };
      }

      if (response.status === 200) {
        const token = data.token?.result || data.token;
        const user = data.user;

        const permissionCodes = apiArrayGetir(user?.permissionCodes);
        const deniedPermissionCodes = apiArrayGetir(user?.deniedPermissionCodes);
        const shiftYetkisiVar = kullaniciShiftYetkiliMi(user);

        localStorage.setItem(
          "token",
          typeof token === "string" ? token : JSON.stringify(token)
        );

        localStorage.setItem("user", JSON.stringify(user));

        localStorage.setItem("userName", user?.adi || "");
        localStorage.setItem("userSurname", user?.soyadi || "");
        localStorage.setItem("userEmail", user?.email || "");

        localStorage.setItem("authData", JSON.stringify(data));

        localStorage.setItem(
          "permissionCodes",
          JSON.stringify(permissionCodes)
        );

        localStorage.setItem(
          "deniedPermissionCodes",
          JSON.stringify(deniedPermissionCodes)
        );

        localStorage.setItem(
          "shiftYetkisiVar",
          shiftYetkisiVar ? "true" : "false"
        );

        localStorage.setItem("mailboxEmail", data?.mailbox?.email || "");
        localStorage.setItem("mailSessionId", data?.mailbox?.mailSessionId || "");

        if (rememberMe) {
          localStorage.setItem("loginEmail", email);
          localStorage.setItem("loginPassword", password);
        } else {
          localStorage.removeItem("loginEmail");
          localStorage.removeItem("loginPassword");
        }
        console.log("LOGIN MAILBOX:", data?.mailbox);

        // 🔐 Rol bilgisi al
        try {
          const rolResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/Rol/benimrollerim`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const rolData = await rolResponse.json();
          const roller = apiArrayGetir(rolData);

          localStorage.setItem("roles", JSON.stringify(roller));

          const mudurErisimResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/mudur/egitim-dashboard/erisim`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const mudurErisimData = mudurErisimResponse.ok
            ? await mudurErisimResponse.json()
            : { erisimVar: false };
          localStorage.setItem(
            "magazaMuduruMu",
            mudurErisimData?.erisimVar === true ? "true" : "false"
          );

          const personelRoller = [
            "Personel",
            "MagazaMudurYardimcisi",
            "MagazaMuduru",
            "AlanMuduru",
            "BolgeMuduru",
          ];
          let userRole = "yetkisiz";

          if (roller.some((r) => personelRoller.includes(r))) {
            userRole = "personel";
          }
          if (userRole === "yetkisiz" && roller.includes("Aday")) {
            userRole = "aday";
          }

          localStorage.setItem("userRole", userRole);
        } catch (rolError) {
          console.error("Rol alınamadı:", rolError);
          localStorage.removeItem("roles");
          localStorage.setItem("magazaMuduruMu", "false");
          localStorage.setItem("userRole", "yetkisiz");
        }

        const kanGrubuEksik =
          user?.kanGrubu === null ||
          user?.kanGrubu === "" ||
          user?.needKanGrubu === true;

        if (kanGrubuEksik) {
          sessionStorage.setItem("showKanGrubuPopup", "true");
        }
        await checkWelcomeVideo(token);

      } else if (response.status === 400) {
        setShowVerificationPopup(true);
      } else if (response.status === 401) {
        setError("❌ E-posta/telefon veya şifre hatalı!");
      } else {
        setError(data.message || "❌ Giriş başarısız!");
      }
    } catch (err) {
      setError(err.message || "⚠️ Bağlantı hatası!");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    const decoded = jwtDecode(credentialResponse.credential);
    const userData = {
      email: decoded.email,
      firstName: decoded.given_name,
      lastName: decoded.family_name,
      provider: "google",
    };
    setGoogleUser(userData);
    setShowRegisterPopup(true);
  };

  return (
    <>
      {/* 🎥 Hoşgeldin Videosu Popup */}
      <WelcomeVideoPopup
        isOpen={showWelcomeVideo}
        onClose={() => {
          setShowWelcomeVideo(false);
          proceedToApp();
        }}
      />

      {!showRegisterPopup && !showVerificationPopup && !showForgotPassword && (
        <div className="login-popup-overlay">
          <div
            className="login-popup-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="login-close-btn" onClick={onClose}>
              ×
            </button>
            <h2 className="popup-title">Hoş Geldiniz</h2>

            <div className="input-box">
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder=" "
                id="identifier"
                autoComplete="username"
              />
              <label htmlFor="identifier" className="login-identifier-label">
                E-posta veya telefon numarası giriniz Örn:5551234567
              </label>
            </div>

            <div className="input-box">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder=" "
                id="password"
              />
              <label htmlFor="password">Şifre</label>
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="show-hide-toggle"
              >
                {showPassword ? "Gizle" : "Göster"}
              </span>
            </div>

            <div className="login-actions">
              <label>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Beni Hatırla
              </label>
              <span
                className="forgot-password-link"
                onClick={() => setShowForgotPassword(true)}
              >
                Şifremi Unuttum
              </span>
            </div>

            {!(
              window.location.hostname === "localhost" ||
              window.location.hostname.startsWith("192.168.")
            ) && (
                <ReCAPTCHA
                  sitekey={siteKey}
                  onChange={() => setCaptchaVerified(true)}
                  className="captcha-style"
                  hl="tr"
                />
              )}

            <button
              className="login-submit"
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? <span className="loader" /> : "Giriş Yap"}
            </button>

            <div style={{ textAlign: "center", marginTop: "10px" }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() =>
                  setError("❌ Google ile kayıt başarısız oldu")
                }
                useOneTap={false}
                text="signup_with"
                theme="outline"
                shape="pill"
                size="large"
              />
            </div>

            <p className="signup-link">
              Üye Değil misiniz?{" "}
              <span
                onClick={() => {
                  setGoogleUser(null);
                  setShowRegisterPopup(true);
                }}
              >
                Hemen Üye Ol
              </span>
            </p>

            {error && <p className="error-message">{error}</p>}
          </div>
        </div>

      )}

      {showRegisterPopup && !showVerificationPopup && (
        <RegisterPopup
          onClose={() => {
            setShowRegisterPopup(false);
            setGoogleUser(null);
          }}
          openLoginPopup={(email) => {
            setEmail(email);
            setShowRegisterPopup(false);
            setGoogleUser(null);
          }}
          googleData={googleUser}
        />
      )}

      {showVerificationPopup && (
        <EmailVerificationPopup
          email={email}
          onClose={() => setShowVerificationPopup(false)}
          onSuccess={() => {
            setShowVerificationPopup(false);
            setTimeout(() => {
              handleLogin();
            }, 1000);
          }}
        />
      )}

      {showForgotPassword && (
        <ForgotPassword onClose={() => setShowForgotPassword(false)} />
      )}
    </>
  );
}
